import './index.css'
import debounce from 'lodash/debounce'

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'hooks/use-redux'
import scrollIntoView from 'scroll-into-view-if-needed'
import type { ClientLibrary } from 'types/client-library'
import type { Track } from 'types/track'
import emptySearch1x from 'assets/images/empty-search.png'
import emptySearch2x from 'assets/images/empty-search@2x.png'
import emptyTracks1x from 'assets/images/empty-tracks.png'
import emptyTracks2x from 'assets/images/empty-tracks@2x.png'
import {
  clientLibraryFeaturedTracksLoad,
  clientLibraryTracksLoad,
  clientLibraryTracksLoadNext,
  clientLibraryTracksSet,
  clientLibraryTracksUpdate,
} from '@disco/state-management/actions/client-library-track-actions'
import {
  getClientLibrary,
  getClientLibraryTracks,
} from '@disco/state-management/selectors/client-libraries'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'
import { moveItems } from '@disco/utils/immutable'
import LibraryMembershipTooltip from 'components/common/library-membership-tooltip'
import Waveform from 'components/common/player/waveform'
import Row from 'components/common/row'
import TrackInfo from 'components/common/track-info'
import DocumentHeadTags from 'components/document-head-tags'
import { Tab, Tabs } from 'components/ui'
import Button from 'components/ui/button'
import Checkbox from 'components/ui/checkbox'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import EmptyState from 'components/ui/empty-state'
import Flex from 'components/ui/flex'
import { HoverProvider } from 'components/ui/hover-provider'
import InfiniteScroll from 'components/ui/infinite-scroll'
import Input from 'components/ui/input'
import PageContainer from 'components/ui/page-container'
import Tooltip from 'components/ui/tooltip'

const LibrariesLibraryTracks = (): React.ReactNode => {
  const dispatch = useDispatch()
  const params = useParams<{ libraryId: string }>()
  const libraryId: number = parseInt(params.libraryId!, 10)
  const library = useSelector(state => getClientLibrary(state, libraryId))
  const tracks = useSelector(state => getClientLibraryTracks(state, libraryId))
  const typeData = useComposerInsteadOfArtist().value
  const [selectedTab, setSelectedTab] = useState(0)
  const [query, setQuery] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoadingNext, setIsLoadingNext] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const itemsRef = useRef([])
  useEffect(() => {
    // @ts-expect-error check for bugs
    if (tracks && tracks.list) {
      // @ts-expect-error
      itemsRef.current = itemsRef.current.slice(0, tracks.list.length)
    }
  }, [tracks])
  const [scrollTarget, setScrollTarget] = useState(null)
  useLayoutEffect(() => {
    if (scrollTarget) {
      scrollIntoView(scrollTarget, {
        behavior: 'smooth',
        scrollMode: 'if-needed',
      })
    }

    return () => {
      setScrollTarget(null)
    }
  }, [scrollTarget])
  const debouncedSetSearchTerm = useRef(
    debounce((value: string) => {
      setIsSearching(false)
      setSearchTerm(value)
    }, 300),
  ).current

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel()
    }
  }, [debouncedSetSearchTerm])

  useEffect(() => {
    if (selectedTab === 0) {
      const controller = new AbortController()
      abortControllerRef.current = controller
      dispatch(
        clientLibraryTracksLoad(libraryId, searchTerm, controller.signal),
      )
    }

    if (selectedTab === 1) {
      abortControllerRef.current = null
      dispatch(clientLibraryFeaturedTracksLoad(libraryId, ''))
    }

    setIsLoadingNext(false)

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [dispatch, libraryId, searchTerm, selectedTab])

  const updateSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoadingNext(false)
    setIsSearching(true)
    setQuery(event.target.value)
    debouncedSetSearchTerm(event.target.value)
  }

  const toggleIsFeatured = (track: Track) => () => {
    let add = []
    let remove = []

    if (track['is_featured']) {
      remove.push(track['id'])
    } else {
      add.push(track['id'])
    }

    dispatch(clientLibraryTracksUpdate(libraryId, add, remove))
  }

  const loadNext = (next: string) => {
    setIsLoadingNext(true)
    dispatch(clientLibraryTracksLoadNext(libraryId, next))
  }

  const renderContent = (tracks: {
    loading: boolean
    loaded: boolean
    nextPage: string | null
    list: Track[]
  }) => (!isLoadingNext && tracks.loading ? 'Loading...' : renderTracks(tracks))

  const renderTracks = (tracks: {
    loading: boolean
    loaded: boolean
    nextPage: string | null
    list: Track[]
  }) =>
    tracks.loaded && tracks.list.length === 0 ? (
      renderEmptyState()
    ) : (
      <InfiniteScroll next={tracks.nextPage} loadNext={loadNext}>
        {tracks.list.map((track: Track, index: number) => {
          const tooltip = track['is_featured']
            ? 'Remove from featured'
            : 'Feature this track'
          const cloneTrack = { ...track }

          if (typeData === 'composer') {
            cloneTrack.artist = cloneTrack.metadata.Composer
          }

          const featuredTracks = tracks.list.filter(
            track => track['is_featured'],
          )
          return (
            <div
              key={track['id']}
              ref={node => (itemsRef.current[index] = node)}
            >
              <Row columnsSize={[12]}>
                <div>
                  <HoverProvider>
                    <Flex spacing="large">
                      {selectedTab === 1 && (
                        <div className="ClientLibrariesLibraryTracks-index">
                          {(index + 1).toString().padStart(2, '0')}
                        </div>
                      )}
                      <Flex.Content>
                        <TrackInfo location="explorer" track={track} />
                      </Flex.Content>
                      <LibraryMembershipTooltip
                        libraryId={libraryId}
                        trackId={track['id']}
                      />
                      <Waveform
                        className="flex-waveform"
                        track={track}
                        barWidth={1.12}
                        barGap={2.18 - 1.12}
                        noAnimation
                        noSeekClick
                      />
                      {selectedTab === 1 && track['is_featured'] && (
                        // @ts-expect-error
                        <CustomDropdown
                          className="dropdown-width--150"
                          options={featuredTracks.map((_, index: number) => ({
                            label: (index + 1).toString(),
                            value: index,
                          }))}
                          placeholder="Change order"
                          onChange={option => {
                            const items = moveItems(
                              featuredTracks.map(track => track['id']),
                              [index],
                              option.value > index
                                ? option.value + 1
                                : option.value,
                            )
                            dispatch(clientLibraryTracksSet(libraryId, items))
                            setScrollTarget(itemsRef.current[index])
                          }}
                        />
                      )}
                      <Tooltip content={tooltip}>
                        <Checkbox
                          checked={track['is_featured']}
                          onChange={toggleIsFeatured(track)}
                        />
                      </Tooltip>
                    </Flex>
                  </HoverProvider>
                </div>
              </Row>
            </div>
          )
        })}
      </InfiniteScroll>
    )

  const renderEmptyState = () =>
    !isSearching && query === '' ? (
      <EmptyState>
        <img
          srcSet={`${emptyTracks1x}, ${emptyTracks2x} 2x`}
          width="200"
          height="194"
        />
        {selectedTab === 0 && (
          <>
            <p>
              No tracks here yet! To add tracks, select content on the “Content”
              page.
            </p>
            <Button
              appearance="primary"
              caption="Add content"
              link={`/catalogs/${libraryId}/content`}
            />
          </>
        )}
        {selectedTab === 1 && (
          <p>
            No tracks here yet! To add tracks, select them in the “All tracks”
            tab.
          </p>
        )}
      </EmptyState>
    ) : (
      <EmptyState>
        <img
          srcSet={`${emptySearch1x}, ${emptySearch2x} 2x`}
          width="177"
          height="160"
        />
        <p>
          Sorry! We couldn’t find any tracks for that search. Try another query,
          or to add more tracks, add more content in the “Content” page.
        </p>
        <Button
          appearance="primary"
          caption="Add content"
          link={`/catalogs/${libraryId}/content`}
        />
      </EmptyState>
    )

  return (
    <PageContainer.Section
      title="Tracks"
      description="Here you can select the tracks you’d like featured on your Catalog home page. Search through the tracks you’ve chosen to include in the Catalog and select using the checkbox."
    >
      {library && !library['loading'] && (
        <DocumentHeadTags title={`${library['name']} | Tracks | DISCO`} />
      )}
      <Tabs
        className="Tabs--modal"
        index={selectedTab}
        onChange={(index: number) => {
          if (index === 1) {
            debouncedSetSearchTerm.cancel()
            setQuery('')
            setSearchTerm('')
          }
          setSelectedTab(index)
        }}
      >
        <Tab label="All tracks">
          <Row columnsSize={[3]}>
            <div>
              <Input
                icon="search"
                placeholder="Search"
                value={query}
                onChange={updateSearchQuery}
              />
            </div>
          </Row>
          {tracks && renderContent(tracks.all)}
        </Tab>
        <Tab label="Featured tracks">
          {tracks && renderContent(tracks.featured)}
        </Tab>
      </Tabs>
    </PageContainer.Section>
  )
}

export default LibrariesLibraryTracks
